import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Row, Select, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IPaginationBaseState, Storage, Translate } from 'react-jhipster';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEleves, getFicheEleves, setSearchCriteria } from './eleve.reducer';
import ResetStatusHandler from './ResetStatusHandler';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { getAnneeScolaire } from '../annee-scolaire/annee-scolaire.reducer';
import { StatusAnneeScolaire } from 'app/shared/model/anneeScolaire.model';

interface IEleveHeader {
  paginationState: IPaginationBaseState;
  anneeScolaireId?: string;
  ecoleId?: string;
}

const EleveHeader: React.FC<IEleveHeader> = ({ paginationState, anneeScolaireId, ecoleId }) => {
  const [form] = Form.useForm();
  const [loadingList, setLoadingList] = useState(false);
  const dispatch = useAppDispatch();
  const classeList = useAppSelector(state => state.classe.entities);
  const anneeScolaire = useAppSelector(state => state.anneeScolaire.entity);
  const loading = useAppSelector(state => state.eleve.loading);
  const isNotComptable = !useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.COMPTABLE]));
  const isNotSurveillantGeneral = !useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.SURVEILLANT_GENERAL])
  );
  const isNotSurveillant = !useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.SURVEILLANT]));
  const searchCriteria = useAppSelector(state => state.eleve.searchCriteria);

  const buildDownloadUrl = criteria => {
    const params = new URLSearchParams();
    params.set('ecoleId', ecoleId);
    if (anneeScolaireId) {
      params.set('anneeScolaireId', anneeScolaireId);
    }
    ['matricule', 'nom', 'prenom', 'classe', 'etatPaiement', 'statutPresence'].forEach(key => {
      if (criteria?.[key]) {
        params.set(key, criteria[key]);
      }
    });
    return `/api/eleves/download?${params.toString()}`;
  };

  useEffect(() => {
    if (anneeScolaireId) {
      dispatch(getAnneeScolaire(anneeScolaireId));
    }
  }, [anneeScolaireId]);

  const token = Storage.local.get('jhi-authenticationToken') || Storage.session.get('jhi-authenticationToken');
  const [apiUrl, setApiUrl] = useState(buildDownloadUrl(searchCriteria));

  useEffect(() => {
    form.setFieldsValue({
      matricule: searchCriteria?.matricule || undefined,
      prenom: searchCriteria?.firstName || undefined,
      nom: searchCriteria?.lastName || undefined,
      classe: searchCriteria?.classe || undefined,
      statutPresence: searchCriteria?.statutPresence || undefined,
      etatPaiement: searchCriteria?.etatPaiement || undefined,
    });
    setApiUrl(buildDownloadUrl(searchCriteria));
  }, [form, searchCriteria, anneeScolaireId, ecoleId]);

  const handleChange = changedValues => {
    const hasClearedField = Object.values(changedValues || {}).some(value => value === undefined || value === null || value === '');
    if (!hasClearedField) {
      return;
    }

    onSearch(form.getFieldsValue());
  };

  const onSearch = val => {
    const entity = {
      ...searchCriteria,
      ...val,
      anneeScolaireId,
      ecoleId,
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
    };
    dispatch(
      setSearchCriteria({
        ...entity,
        firstName: entity.prenom || searchCriteria.firstName,
        lastName: entity.nom || searchCriteria.lastName,
      })
    );
    if (anneeScolaireId) {
      dispatch(getEleves(entity));
    } else {
      dispatch(getFicheEleves(entity));
    }
    setApiUrl(buildDownloadUrl(entity));
  };

  const downloadFile = () => {
    setLoadingList(true);
    axios({
      url: apiUrl,
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + token,
      },
      responseType: 'blob',
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'élèves.xlsx');
      document.body.appendChild(link);
      link.click();
      setLoadingList(false);
    });
  };
  const shouldDisable = anneeScolaireId === 'null';

  return (
    <div>
      <Row justify="end" align="middle" className="my-2 py-2">
        <>
          {isNotComptable && isNotSurveillantGeneral && !anneeScolaireId && (
            <Button type="primary" className="jh-create-entity mx-1" style={{ backgroundColor: '#F28E19', border: '#F28E19' }}>
              <Link to={`/eleve/${ecoleId}/new`} id="jh-create-entity" data-cy="entityCreateButton">
                <FontAwesomeIcon icon="plus" />
                &nbsp;
                <Translate contentKey="korischoolApp.eleve.home.createLabel">Create new Eleve</Translate>
              </Link>
            </Button>
          )}
          {isNotComptable && isNotSurveillant && anneeScolaireId && anneeScolaire.statutAnneeScolaire !== StatusAnneeScolaire.CLOTUREE && (
            <ResetStatusHandler anneeScolaireId={anneeScolaireId} ecoleId={ecoleId} />
          )}
        </>
      </Row>
      <Form
        form={form}
        style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: anneeScolaireId ? 10 : 0 }}
        onFinish={onSearch}
        onValuesChange={handleChange}
      >
        <Form.Item name="matricule">
          <Input placeholder="Matricule" allowClear />
        </Form.Item>
        <Form.Item name="prenom">
          <Input placeholder="Prénoms" allowClear />
        </Form.Item>
        <Form.Item name="nom">
          <Input placeholder="Nom" allowClear />
        </Form.Item>
        {anneeScolaireId && (
          <>
            <Form.Item name="classe">
              <Select placeholder="Classe" style={{ width: '100%' }} optionFilterProp="children" allowClear>
                {classeList.map((classe, i) => (
                  <Select.Option key={classe.id} value={classe.id}>
                    {' '}
                    {classe.nom}{' '}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="statutPresence">
              <Select placeholder="Status" allowClear>
                <Select.Option value="PRESENT">
                  <Tag color="green">Present</Tag>
                </Select.Option>
                <Select.Option value="ABSENT">
                  <Tag color="red">Absent</Tag>
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="etatPaiement">
              <Select placeholder="Etat" allowClear style={{ width: '110px' }}>
                <Select.Option value="A_JOUR">
                  <Tag color="green">A JOUR</Tag>
                </Select.Option>
                <Select.Option value="NON_A_JOUR">
                  <Tag color="red">NON A JOUR</Tag>
                </Select.Option>
              </Select>
            </Form.Item>
          </>
        )}
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />} className="mx-1"></Button>
        </Form.Item>

        {anneeScolaireId && (
          <Button disabled={shouldDisable} className="mx-1" onClick={downloadFile} type="primary" loading={loadingList}>
            <FontAwesomeIcon icon={faUpload} />
            &nbsp; {loading ? 'téléchargement en cours...' : 'Exporter'}
          </Button>
        )}
      </Form>
    </div>
  );
};

export default EleveHeader;
